<template>
  <!-- Overlay -->
  <div class="relative w-full">
    <div
      class="fixed bottom-12 right-auto w-full xl:w-auto justify-center xl:absolute xl:right-0 xl:bottom-auto"
    >
      <MapZoom></MapZoom>
    </div>
    <div
      v-if="mapSettingsStore.showLargeFleets"
      class="fixed bottom-16 pb-3 xl:pb-0 right-auto w-full xl:w-auto flex justify-end xl:absolute xl:right-0 xl:top-8 pointer-events-none"
    >
      <MapFleets></MapFleets>
    </div>
    <SettleLegend v-if="shipActionStore.pendingAction === 'settle'" />
    <MapTargetHud />
    <MapRouteEditorHud />
    <MapRouteNameTooltip />
    <MapSideBar />
    <!-- Top Bar -->
  </div>
  <!-- WebGL Container -->
  <div
    class="webgl-container content-height-limit-map z-10"
    :class="{ 'cursor-crosshair': isTargetingMode }"
    ref="container"
    @contextmenu.prevent
  ></div>
  <!-- Gradient overlay -->
  <div class="map-gradient-overlay"></div>
  <MapToolTip :tool-tip-visible="mapStore.toolTipVisible"></MapToolTip>
  <MapPlayerNameToolTip></MapPlayerNameToolTip>
  <AreaSelectionOptions v-if="mapStore.areaActionSelecting" />
  <div class="absolute bottom-2 left-1 hidden md:block text-xs text-gray-400">
    {{ mapStore.mouseXPos }} / {{ mapStore.mouseYPos }}<template v-if="fpsReady">
    <template v-if="isDev"> | <span :class="fpsColorClass">{{ currentFps }} fps</span></template>
    | <span :class="latencyColorClass">{{ gameStore.websocketLatency }}ms</span>
    <template v-if="isDev"> | {{ wsBudget.toFixed(1) }}wb</template></template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import * as THREE from 'three'
import { useMapStore } from '@/stores/mapStore.ts'
import type MapCoordinate from '@/types/MapCoordinate.ts'
import emitter from '@/models/eventBus.ts'
import MapToolTip from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapToolTip.vue'
import MapZoom from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapZoom.vue'
import MapFleets from '@/components/Sections/Map/MapFleets.vue'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { ShipActionPath } from '@/models/Map/MapShipActionPath.ts'
import { MapAllShipsActionPaths } from '@/models/Map/MapAllShipsActionPaths.ts'
import { MapPortalConnections } from '@/models/Map/MapPortalConnections.ts'
import { MapRouteEditorPath } from '@/models/Map/MapRouteEditorPath.ts'
import { MapRoutes } from '@/models/Map/MapRoutes.ts'
import MapTargetHud from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapTargetHud.vue'
import MapRouteEditorHud from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapRouteEditorHud.vue'
import MapRouteNameTooltip from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapRouteNameTooltip.vue'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import { useRoute } from 'vue-router'
import type PlayerRelation from '@/types/PlayerRelation.ts'
import { MapMouseController } from '@/models/Map/MapMouseController.ts'
import { useShipStore } from '@/stores/shipStore.ts'
import { useFleetStore } from '@/stores/fleetStore.ts'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import { useMapShipStore } from '@/stores/mapShipStore.ts'
import { useMapPlanetOwnerStore } from '@/stores/mapPlanetOwnerStore.ts'
import { useMapPlanetStore } from '@/stores/mapPlanetStore.ts'
import { useMapStarStore } from '@/stores/mapStarStore.ts'
import { useStarStore } from '@/stores/starStore.ts'
import { useMapPendingSettleStore } from '@/stores/mapPendingSettleStore.ts'
import AreaSelectionOptions from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/AreaSelectionOptions.vue'
import MapPlayerNameToolTip from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapPlayerNameToolTip.vue'
import { MapBlackHole } from '@/models/Map/MapBlackHole.ts'
import MapSideBar from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/MapSideBar/MapSideBar.vue'
import SettleLegend from '@/components/Sections/Map/Galaxy/WebGLMap/MapObjects/SettleLegend.vue'
import { MapCrossHairInit, MapCrossHairUpdate } from '@/models/Map/MapCrosshair.ts'
import { useMapPortalStore } from '@/stores/mapPortalStore.ts'
import { usePortalStore } from '@/stores/portalStore.ts'
import { MapScanRadius } from '@/models/Map/MapScanRadius.ts'
import { useMapSettingsStore } from '@/stores/mapSettingsStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useMapTerritoryStore } from '@/stores/mapTerritoryStore.ts'
import { useRouteEditorStore } from '@/stores/routeEditorStore.ts'
import { useRouteStore } from '@/stores/routeStore.ts'
import { useMapCameraStore } from '@/stores/mapCameraStore.ts'
import { debounce, throttle } from 'lodash'
import { zoomFactor } from '@/enums/zoomFactor'
import { getWebSocketUpdateQueue } from '@/models/WebSocketUpdateQueue'

// Store references and states
const mapStore = useMapStore()
const mapCoordinateStore = useMapCoordinateStore()
const mapShipStore = useMapShipStore()
const mapPlanetOwnerStore = useMapPlanetOwnerStore()
const mapPlanetStore = useMapPlanetStore()
const mapStarStore = useMapStarStore()
const starStore = useStarStore()
const mapPendingSettleStore = useMapPendingSettleStore()
const mapPortalStore = useMapPortalStore()
const mapSettingsStore = useMapSettingsStore()
const gameStore = useGameStore()
const mapTerritoryStore = useMapTerritoryStore()
const routeEditorStore = useRouteEditorStore()
const routeStore = useRouteStore()
const mapCameraStore = useMapCameraStore()

const shipStore = useShipStore()
const fleetStore = useFleetStore()
const planetStore = usePlanetStore()
const portalStore = usePortalStore()
const shipActionStore = useShipActionStore()
const container = ref<HTMLDivElement | null>(null)
const toolTipVisible = ref(false)
const currentFps = ref(0)
const fpsReady = ref(false) // Only show FPS after hitting 60 once
const wsBudget = ref(1) // Adaptive WebSocket processing budget in ms
const isDev = import.meta.env.DEV
const route = useRoute()

// FPS color: 50+ green, 40+ yellow, 30+ orange, <30 red
const fpsColorClass = computed(() => {
  if (currentFps.value >= 50) return 'text-green-400'
  if (currentFps.value >= 40) return 'text-yellow-400'
  if (currentFps.value >= 30) return 'text-orange-400'
  return 'text-red-400'
})

// Latency color: <300 green, <600 yellow, <1000 orange, >1000 red
const latencyColorClass = computed(() => {
  const latency = gameStore.websocketLatency
  if (latency < 300) return 'text-green-400'
  if (latency < 600) return 'text-yellow-400'
  if (latency < 1000) return 'text-orange-400'
  return 'text-red-400'
})

// Check if we're in a targeting mode that requires clicking on the map
// Note: 'settle' is excluded because the crosshair should only show over eligible planets
const isTargetingMode = computed(() => {
  const targetingActions = ['move', 'attack', 'follow', 'followAttack']
  return targetingActions.includes(shipActionStore.pendingAction)
})

// The mouseController sets up event listeners in its constructor and manages mouse/touch interactions
// It doesn't need to be called directly but must be instantiated for the map controls to work
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let mouseController: MapMouseController
let scene: THREE.Scene
let camera: THREE.PerspectiveCamera
let renderer: THREE.WebGLRenderer
let mapScanRadius: MapScanRadius
let allShipsActionPaths: MapAllShipsActionPaths
let portalConnections: MapPortalConnections
let routeEditorPath: MapRouteEditorPath
let mapRoutes: MapRoutes
let blackHole: MapBlackHole
let backPlane: THREE.Mesh
let isContextLost = false

/**
 * Handle WebGL context loss (common on mobile Safari)
 */
const handleContextLost = (event: Event) => {
  event.preventDefault()
  isContextLost = true
  console.warn('WebGL context lost - rendering paused')
}

/**
 * Handle WebGL context restoration
 */
const handleContextRestored = () => {
  isContextLost = false
  console.log('WebGL context restored - rendering resumed')

  // Force re-render after context restoration
  if (renderer && scene && camera) {
    renderer.render(scene, camera)
  }
}

/**
 * Handle keyboard shortcuts for zoom levels
 */
const handleZoomKeyDown = (event: KeyboardEvent) => {
  // Only handle when on map page
  if (!route.name?.toString().startsWith('MapGalaxy')) return

  // Don't handle if user is typing in an input or textarea
  const target = event.target as HTMLElement
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return

  const zoomMap: Record<string, number> = {
    '1': zoomFactor.Planet,
    '2': zoomFactor.Solar,
    '3': zoomFactor.Local,
    '4': zoomFactor.Sector,
    '5': zoomFactor.Quadrant,
    '6': zoomFactor.Max
  }

  if (zoomMap[event.key]) {
    mapStore.zoomFactor = zoomMap[event.key]
  }
}

/**
 * Initialize the map
 */
onMounted(async () => {
  try {
    /**
     * Create the WebGL renderer and camera
     */
    renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
      alpha: true
    })
    
    if (container.value) {
      renderer.setSize(container.value.clientWidth, container.value.clientHeight)
      container.value.appendChild(renderer.domElement)
    }

    // Handle WebGL context loss/restore (critical for mobile Safari)
    renderer.domElement.addEventListener('webglcontextlost', handleContextLost, false)
    renderer.domElement.addEventListener('webglcontextrestored', handleContextRestored, false)
  } catch (error) {
    // Handle WebGL renderer creation errors
    gameStore.showModal('error', error)
    return
  }

  camera = new THREE.PerspectiveCamera(
    45,
    container.value ? container.value.clientWidth / container.value.clientHeight : 1,
    1,
    500
  )
  camera.position.set(0, 0, 400)
  camera.lookAt(0, 0, 0)

  camera.position.x = 1000
  camera.position.y = 1000
  
  // Initialize camera position in mapStore
  mapStore.cameraXPos = camera.position.x
  mapStore.cameraYPos = camera.position.y

  scene = new THREE.Scene()
  mapStore.scene = scene
  mapStore.camera = camera
  
  // Set scene to sort objects during rendering for proper transparency
  renderer.sortObjects = true

  /** Store the scene in the mapCoordinateStore */
  mapCoordinateStore.scene = scene

  /** Load the Instanced Meshes */
  mapPlanetOwnerStore.initPlanetOwnerInstancedMesh(10000) // Small
  if (mapPlanetOwnerStore.planetOwnerInstancedMesh) {
    scene.add(mapPlanetOwnerStore.planetOwnerInstancedMesh)
  }

  mapPlanetStore.initPlanetInstancedMesh(10000) // Small
  if (mapPlanetStore.planetInstancedMesh) {
    scene.add(mapPlanetStore.planetInstancedMesh)
  }
  
  mapStarStore.initStarInstancedMesh(1200)
  if (mapStarStore.starInstancedMesh) {
    scene.add(mapStarStore.starInstancedMesh)
  }
  
  // Initialize ships after stars so they render on top
  mapShipStore.initShipInstancedMesh(20000) // Enough?
  if (mapShipStore.shipInstancedMesh) {
    scene.add(mapShipStore.shipInstancedMesh)
  }

  mapPendingSettleStore.initObjectInstancedMesh(10000)
  if (mapPendingSettleStore.objectInstancedMesh) {
    scene.add(mapPendingSettleStore.objectInstancedMesh)
  }

  mapPortalStore.initPortalInstancedMesh(100)
  if (mapPortalStore.portalInstancedMesh) {
    scene.add(mapPortalStore.portalInstancedMesh)
  }

  // Initialize territory overlay mesh
  mapTerritoryStore.initTerritoryMesh()
  if (mapTerritoryStore.territoryMesh) {
    scene.add(mapTerritoryStore.territoryMesh)
    mapTerritoryStore.setVisible(mapSettingsStore.showTerritoryOverlay)
    mapTerritoryStore.setOpacity(mapSettingsStore.territoryOpacity / 100)
  }

  /** Tests **/
  // Test objects are created and immediately removed to verify instanced mesh functionality
  mapPlanetOwnerStore.addPlanetOwner(-0, -0, 0x000000)
  const planetInstanceId = mapPlanetStore.addPlanet(-0, -0, 1, 5)
  const starInstanceId = mapStarStore.addStar(-0, -0, 1, 4)
  mapPendingSettleStore.addObject(-0, -0, 0x000000)
  const portalInstanceId = mapPortalStore.addPortal(-0, -0)
  mapShipStore.addShip(-0, -0, 0x000000)
  
  /**
   * Remove test objects
   */
  mapPlanetStore.removePlanet(planetInstanceId)
  mapStarStore.removeStar(starInstanceId)
  mapPortalStore.removePortal(portalInstanceId)

  /**
   * Add black hole
   */
  blackHole = new MapBlackHole(scene)

  /**
   * Initialize scan radius visualization
   */
  mapScanRadius = new MapScanRadius(scene)

  // Schedule scan radii refresh after initial data is processed
  // This ensures scan ranges show on first load (waits for pending queue to empty)
  let scanRadiiRetries = 0
  const refreshScanRadiiWhenReady = () => {
    scanRadiiRetries++
    // Keep trying for up to 10 seconds (40 retries * 250ms)
    if (scanRadiiRetries > 40) return
    // Wait for queue to empty and sync to complete
    if (mapCoordinateStore.getPendingCount() > 0 || !gameStore.websocketInitialSyncCompleted) {
      setTimeout(refreshScanRadiiWhenReady, 250)
      return
    }
    mapScanRadius.updateScanRadii()
    // Keep retrying a few more times in case more data arrives
    if (scanRadiiRetries < 10) {
      setTimeout(refreshScanRadiiWhenReady, 500)
    }
  }
  setTimeout(refreshScanRadiiWhenReady, 500)

  /**
   * CrossHairs
   */
  new MapCrossHairInit(scene, camera)

  /**
   * Init Ship Action Path
   */
  mapStore.shipActionPath = new ShipActionPath(scene)

  /**
   * Init All Ships Action Paths
   */
  allShipsActionPaths = new MapAllShipsActionPaths(scene)

  /**
   * Init Portal Connections
   */
  portalConnections = new MapPortalConnections(scene)

  /**
   * Init Route Editor Path
   */
  routeEditorPath = new MapRouteEditorPath(scene)

  /**
   * Init Map Routes (displays saved routes)
   */
  mapRoutes = new MapRoutes(scene)
  mapRoutes.update()

  /**
   * Init Mouse Controller
   */
  if (container.value) {
    mouseController = new MapMouseController(container.value, camera, scene, renderer)
  }

  // Debounced scan radii update to avoid rebuilding on every WebSocket event
  // Lower debounce is fine since computation runs in web worker
  const debouncedScanRadiiUpdate = debounce(() => {
    if (mapScanRadius) {
      mapScanRadius.updateScanRadii()
    }
  }, 100)

  // Listen to mapCoordinateUpdate events from Websockets
  emitter.on('mapCoordinateUpdate', (data) => {
    mapCoordinateStore.process(data as MapCoordinate)
    // Update scan radii when map coordinates change (debounced)
    debouncedScanRadiiUpdate()
  })

  // Listen to mapRelationUpdate events from Websockets
  emitter.on('mapRelationUpdate', (data) => {
    mapCoordinateStore.reDrawPlayerAssets((data as PlayerRelation).playerId)
  })

  /**
   * Listen to mouse events
   */
  if (container.value) {
    window.addEventListener('resize', resizeRenderer)
    window.addEventListener('keydown', handleZoomKeyDown)
  }

  // Backplane for detecting coordinates in space
  // Use a larger size to ensure it covers the entire galaxy grid plus some buffer
  const backPlaneSize = Math.max(gameStore.galaxyTileGridSize * 2, 10000)
  const backPlaneGeometry = new THREE.PlaneGeometry(backPlaneSize, backPlaneSize)
  const backPlaneMaterial = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0
  })
  backPlane = new THREE.Mesh(backPlaneGeometry, backPlaneMaterial)
  backPlane.name = 'backPlane'
  backPlane.position.z = -0.2
  scene.add(backPlane)

  /**
   * Start the animation loop
   */
  // FPS counter variables
  let frameCount = 0
  let lastFpsLogTime = performance.now()
  let lastFrameTime = performance.now()
  const FPS_LOG_INTERVAL = 1000 // Log FPS every second

  function animate() {
    requestAnimationFrame(animate)

    // Skip rendering if WebGL context is lost
    if (isContextLost) {
      return
    }

    // Calculate delta time
    const now = performance.now()
    const delta = (now - lastFrameTime) / 1000
    lastFrameTime = now

    // Update animations
    mapShipStore.updateTime(delta)
    if (blackHole) {
      blackHole.update(delta)
    }
    // Update portal rotation (consolidated from separate animation loop)
    if (mapPortalStore.portalInstancedMesh) {
      const material = mapPortalStore.portalInstancedMesh.material as THREE.ShaderMaterial
      material.uniforms.time.value += delta / 8
    }

    // Render the scene FIRST - always prioritize smooth rendering
    renderer.render(scene, camera)

    // FPS tracking for UI display (capped at 60)
    frameCount++
    if (now - lastFpsLogTime >= FPS_LOG_INTERVAL) {
      currentFps.value = Math.min(60, Math.ceil(frameCount / ((now - lastFpsLogTime) / 1000)))
      frameCount = 0
      lastFpsLogTime = now
      // Only show FPS indicator after reaching stable fps (avoids misleading initial values)
      if (currentFps.value >= 50) {
        fpsReady.value = true
      }
    }

    // Process WebSocket updates AFTER rendering
    // During initial load: process ALL pending for faster loading
    // After initial load: use adaptive budget to maintain 60fps
    const wsQueue = getWebSocketUpdateQueue()
    const isInitialLoadComplete =
      gameStore.websocketInitialSyncCompleted &&
      wsQueue.getPendingCount() === 0 &&
      mapCoordinateStore.getPendingCount() === 0

    if (isInitialLoadComplete) {
      // Normal adaptive budget after initial load
      const frameTimeMs = delta * 1000
      if (frameTimeMs > 18) {
        wsBudget.value = Math.max(0.1, wsBudget.value - 2)
      } else if (frameTimeMs < 17 && currentFps.value >= 58) {
        wsBudget.value = Math.min(6, wsBudget.value + 0.033)
      }
      // Update WebSocket queue budget (it self-processes via requestAnimationFrame)
      wsQueue.setBudget(wsBudget.value * 0.4)
      mapCoordinateStore.processPendingWithBudget(wsBudget.value * 0.6)
    } else {
      // During initial load: process ALL pending items (full CPU)
      wsQueue.processAll()
      mapCoordinateStore.processAllPending()
    }
  }

  animate()

  /**
   * Restore saved camera position, or go to home planet on first load
   */
  mapCameraStore.loadFromStorage()
  if (mapCameraStore.canRestore) {
    camera.position.x = mapCameraStore.savedCameraX
    camera.position.y = mapCameraStore.savedCameraY
    mapStore.cameraXPos = camera.position.x
    mapStore.cameraYPos = camera.position.y
    mapStore.zoomFactor = Math.max(0.1, Math.min(10, mapCameraStore.savedZoomFactor))
  } else if (planetStore.planets.size > 0) {
    const firstPlanet = planetStore.planets.values().next().value
    if (firstPlanet) {
      mapStore.gotoX = firstPlanet.xPos
      mapStore.gotoY = firstPlanet.yPos
    }
  }

  /**
   * Load the visible sectors
   */
  mapStore.loadVisibleSectors()

  /**
   * Recalculate star ownership after map load
   * This ensures all stars have correct ownership based on planet distribution
   */
  for (const star of starStore.stars.values()) {
    starStore.recalculateStarOwnership(star.id)
  }

  /**
   * Update territories if overlay is enabled
   */
  if (mapSettingsStore.showTerritoryOverlay) {
    mapTerritoryStore.updateTerritories(starStore.stars)
  }

  renderer.render(scene, camera)
  camera.updateProjectionMatrix()
})

/**
 * Dispose the renderer and remove event listeners
 */
onUnmounted(() => {
  if (renderer) {
    // Remove WebGL context event listeners
    renderer.domElement.removeEventListener('webglcontextlost', handleContextLost)
    renderer.domElement.removeEventListener('webglcontextrestored', handleContextRestored)
    renderer.dispose()
  }
  if (container.value) {
    window.removeEventListener('resize', resizeRenderer)
    window.removeEventListener('keydown', handleZoomKeyDown)
  }
  if (allShipsActionPaths) {
    allShipsActionPaths.dispose()
  }
  if (portalConnections) {
    portalConnections.dispose()
  }
  if (routeEditorPath) {
    routeEditorPath.dispose()
  }
  if (mapRoutes) {
    mapRoutes.dispose()
  }
  if (blackHole) {
    blackHole.dispose()
  }
  if (backPlane) {
    backPlane.geometry.dispose()
    ;(backPlane.material as THREE.MeshBasicMaterial).dispose()
  }
})

/**
 * Resize the renderer when the window is resized
 */
const resizeRenderer = () => {
  if (container.value && renderer) {
    if (container.value.clientWidth === 0 || container.value.clientHeight === 0) {
      return
    }
    renderer.setSize(container.value.clientWidth, container.value.clientHeight)
    camera.aspect = container.value.clientWidth / container.value.clientHeight
    camera.updateProjectionMatrix() // Update when aspect ratio changes
  }
}

watch(
  () => shipActionStore.requestMapReset,
  (newValue) => {
    if (newValue) {
      mapStore.shipActionPath.resetActionPath()
    }
  }
)

/**
 * Throttled zoom factor application - updates at 60fps during zoom for smooth icon resizing
 */
const throttledZoomApply = throttle((zoomValue: number) => {
  mapCoordinateStore.applyZoomFactorAll(zoomValue)
}, 16)

/**
 * Watch the zoom factor and update the camera
 */
watch(
  () => mapStore.zoomFactor,
  (newValue) => {
    if (camera) {
      // Immediate: camera zoom for visual feedback
      camera.zoom = newValue
      camera.updateProjectionMatrix()

      // Immediate: scan radius and action path updates (lightweight)
      if (mapScanRadius) {
        mapScanRadius.applyZoomFactor()
      }
      if (mapStore.shipActionPath) {
        mapStore.shipActionPath.updateZoom(newValue)
      }
      if (allShipsActionPaths) {
        allShipsActionPaths.onZoomChanged(newValue)
      }

      // Update crosshair scale to maintain constant visual size
      if (mapStore.crossHairVisible && scene) {
        new MapCrossHairUpdate(scene, newValue)
      }

      // Throttled: coordinate scaling at 60fps for smooth icon resizing
      throttledZoomApply(newValue)
    }
  }
)

/**
 * Watch the gotoX and gotoY values and move the camera
 */
const gotoCoordinates = computed(() => ({
  x: mapStore.gotoX,
  y: mapStore.gotoY
}))

watch(
  gotoCoordinates,
  (newCoords) => {
    if (newCoords.x === 0 && newCoords.y === 0) {
      return
    }
    if (!camera) {
      return
    }
    //move camera to new coordinates
    camera.position.x = newCoords.x
    camera.position.y = newCoords.y
    mapStore.cameraXPos = camera.position.x
    mapStore.cameraYPos = camera.position.y
    toolTipVisible.value = false

    mapStore.gotoX = 0
    mapStore.gotoY = 0
    mapStore.crossHairVisible = true
  },
  { deep: true }
)

/**
 * Save camera position changes to persistent storage (debounced)
 */
watch(
  () => [mapStore.cameraXPos, mapStore.cameraYPos, mapStore.zoomFactor],
  ([x, y, zoom]) => {
    if (gameStore.websocketInitialSyncCompleted) {
      mapCameraStore.saveCameraState(x as number, y as number, zoom as number)
    }
  }
)

/**
 * Watch the mouse position and update the store
 */
watch(
  () => route.name,
  (newValue) => {
    if (newValue?.toString().startsWith('MapGalaxy')) {
      // Resize the renderer with delay
      setTimeout(() => {
        resizeRenderer()
      }, 100)
    }
  }
)

/**
 * Rerender the path
 */
watch(
  () => shipActionStore.redrawPath,
  () => {
    if (!shipActionStore.redrawPath) {
      return
    }
    mapStore.shipActionPath.removePendingAction()
    mapStore.shipActionPath.setActionPath()
    shipActionStore.redrawPath = false
  }
)

watch(
  () => shipActionStore.pendingAction,
  (newValue, oldValue) => {
    if (newValue === 'settle') {
      //Mark all planets that are targeted for settlement
      const settleTargets = shipStore.getAllCoordsTargetedForSettlement()
      for (let i = 0; i < settleTargets.length; i++) {
        const target = settleTargets[i]
        const instanceId = mapPendingSettleStore.addObject(target.x, target.y, 0xffffff)
        mapPendingSettleStore.scaleAndPositionObject(
          instanceId,
          target.x,
          target.y,
          mapStore.zoomFactor
        )
      }
      //Mark all planets that can be settled
      const planets = mapCoordinateStore.getSettleablePlanets()

      for (let i = 0; i < planets.length; i++) {
        const target = planets[i]
        //Check if the coordinate is already in the settleTargets array
        if (
          settleTargets.find(
            (coord: { x: number; y: number }) => coord.x === target.xPos && coord.y === target.yPos
          )
        ) {
          continue
        }

        //Define color
        let color = 0x000000
        if (target.compatibility === 'A' || target.compatibility === 'F') {
          color = 0x00ff00 // Light Green
        } else if (target.compatibility === 'B') {
          color = 0xadd8e6 // Light blue
        } else if (target.compatibility === 'C') {
          continue
        } else if (target.compatibility === 'D') {
          color = 0xffff00 // Yellow
        } else if (target.compatibility === 'E') {
          color = 0xff0000 // Red
        }

        const instanceId = mapPendingSettleStore.addObject(target.xPos, target.yPos, color)
        mapPendingSettleStore.scaleAndPositionObject(
          instanceId,
          target.xPos,
          target.yPos,
          mapStore.zoomFactor
        )
      }
    } else if (oldValue === 'settle') {
      //Hide all planets that are targeted for settlement
      mapPendingSettleStore.clearObjects()
    }
  }
)

/**
 * Watch the showScanRange setting and update scan radii
 */
watch(
  () => mapSettingsStore.showScanRange,
  () => {
    if (mapScanRadius) {
      mapScanRadius.updateScanRadii()
    }
  }
)

/**
 * Watch the showAllShipActionPaths setting and toggle display
 */
watch(
  () => mapSettingsStore.showAllShipActionPaths,
  (newValue) => {
    if (allShipsActionPaths) {
      allShipsActionPaths.setVisible(newValue)
    }
  }
)

/**
 * Watch for ship store changes and update action paths
 * Uses updateVersion counter instead of deep watching for better performance
 */
watch(
  () => shipStore.updateVersion,
  () => {
    if (allShipsActionPaths) {
      allShipsActionPaths.onShipsUpdated()
    }
  }
)

/**
 * Watch for fleet store changes and update action paths
 * Uses updateVersion counter instead of deep watching for better performance
 */
watch(
  () => fleetStore.updateVersion,
  () => {
    if (allShipsActionPaths) {
      allShipsActionPaths.onShipsUpdated()
    }
  }
)

/**
 * Watch for portal store changes and update connections
 * Uses updateVersion counter instead of deep watching for better performance
 */
watch(
  () => portalStore.updateVersion,
  () => {
    if (portalConnections) {
      portalConnections.onPortalsUpdated()
    }
  }
)

/**
 * Watch for territory overlay visibility changes
 */
watch(
  () => mapSettingsStore.showTerritoryOverlay,
  (newValue) => {
    mapTerritoryStore.setVisible(newValue)
    if (newValue) {
      // Recalculate ownership for all stars before updating territories
      for (const star of starStore.stars.values()) {
        starStore.recalculateStarOwnership(star.id)
      }
      // Update territories when showing (will use hash to avoid redundant work)
      mapTerritoryStore.updateTerritories(starStore.stars)
    }
  }
)

/**
 * Watch for territory opacity changes and update shader
 */
watch(
  () => mapSettingsStore.territoryOpacity,
  (newValue) => {
    // Convert percentage (0-100) to decimal (0-1)
    mapTerritoryStore.setOpacity(newValue / 100)
  }
)

/**
 * Debounced territory update to avoid recalculating on every change
 */
const debouncedTerritoryUpdate = debounce(() => {
  if (mapSettingsStore.showTerritoryOverlay) {
    // Recalculate ownership for all stars before updating territories
    for (const star of starStore.stars.values()) {
      starStore.recalculateStarOwnership(star.id)
    }
    mapTerritoryStore.updateTerritories(starStore.stars)
  }
}, 500)

/**
 * Watch for star or planet changes and update Voronoi territories
 */
watch(
  () => [starStore.stars.size, mapCoordinateStore.planets.size],
  () => {
    debouncedTerritoryUpdate()
  }
)

/**
 * Watch for route editor path redraw requests
 */
watch(
  () => routeEditorStore.redrawPath,
  () => {
    if (!routeEditorStore.redrawPath) {
      return
    }
    if (routeEditorPath) {
      routeEditorPath.update()
    }
    routeEditorStore.redrawPath = false
  }
)

/**
 * Watch for route editor initialization state changes
 */
watch(
  () => routeEditorStore.initialized,
  (newValue) => {
    if (routeEditorPath) {
      if (!newValue) {
        // Clear route editor visualization when exiting
        routeEditorPath.clearAll()
      }
    }
  }
)

/**
 * Watch for route store changes to update map routes
 */
watch(
  () => routeStore.routes,
  () => {
    if (mapRoutes) {
      mapRoutes.update()
    }
  },
  { deep: true }
)

/**
 * Watch for showRoutes setting changes
 */
watch(
  () => mapSettingsStore.showRoutes,
  () => {
    if (mapRoutes) {
      mapRoutes.update()
    }
  }
)

</script>

<style scoped>
.webgl-container {
  width: 100%;
}

.cursor-crosshair {
  cursor: crosshair !important;

  :deep(canvas) {
    cursor: crosshair !important;
  }
}
</style>
